// === Event Type ===

/**
 * Available personalization event types.
 *
 * @constant
 * @property {string} VIEW - Product view event
 * @property {string} CLICK - Product click event
 * @property {string} IMPRESSION - Product impression event (in viewport)
 * @property {string} ADD_TO_CART - Add to cart event
 * @property {string} REMOVE_FROM_CART - Remove from cart event
 * @property {string} PURCHASE - Purchase event
 * @property {string} SIGNUP - User signup event
 * @property {string} REFERRAL - Referral event
 * @property {string} REWARD_REDEMPTION - Reward redemption event
 * @property {string} REVIEW_WRITE - Product review write event
 */
export const EVENT_TYPE = {
  VIEW: 'View',
  CLICK: 'Click',
  IMPRESSION: 'Impression',
  ADD_TO_CART: 'AddToCart',
  REMOVE_FROM_CART: 'RemoveFromCart',
  PURCHASE: 'Purchase',
  SIGNUP: 'Signup',
  REFERRAL: 'Referral',
  REWARD_REDEMPTION: 'RewardRedemption',
  REVIEW_WRITE: 'ReviewWrite',
  HOME_PAGE_VIEW: 'HomePageView',
} as const;

/**
 * Union type of all event type values.
 */
export type EventType = (typeof EVENT_TYPE)[keyof typeof EVENT_TYPE];

// === Context per eventType ===

/**
 * Available contexts for VIEW events.
 * Describes where the product view occurred.
 *
 * @constant
 */
export const VIEW_CONTEXT = {
  HOME: 'home',
  SEARCH: 'search',
  CATEGORY: 'category',
  RECOMMENDATION: 'recommendation',
  PDP: 'pdp',
  WISHLIST: 'wishlist',
  RECENTLY_VIEWED: 'recently-viewed',
} as const;
export type ViewContext = (typeof VIEW_CONTEXT)[keyof typeof VIEW_CONTEXT];

export const CLICK_CONTEXT = {
  HOME: 'home',
  SEARCH: 'search',
  CATEGORY: 'category',
  RECOMMENDATION: 'recommendation',
  CROSS_SELL: 'cross-sell',
  TRENDING: 'trending',
  NEW_ARRIVALS: 'new-arrivals',
} as const;
export type ClickContext = (typeof CLICK_CONTEXT)[keyof typeof CLICK_CONTEXT];

export const IMPRESSION_CONTEXT = {
  HOME: 'home',
  SEARCH: 'search',
  CATEGORY: 'category',
  RECOMMENDATION: 'recommendation',
  CROSS_SELL: 'cross-sell',
  TRENDING: 'trending',
  NEW_ARRIVALS: 'new-arrivals',
} as const;
export type ImpressionContext = (typeof IMPRESSION_CONTEXT)[keyof typeof IMPRESSION_CONTEXT];

export const ADD_TO_CART_CONTEXT = {
  PDP: 'pdp',
  QUICK_VIEW: 'quick-view',
  RECOMMENDATION: 'recommendation',
  CROSS_SELL: 'cross-sell',
  CART: 'cart',
  WISHLIST: 'wishlist',
  CHECKOUT: 'checkout',
} as const;
export type AddToCartContext = (typeof ADD_TO_CART_CONTEXT)[keyof typeof ADD_TO_CART_CONTEXT];

export const REMOVE_FROM_CART_CONTEXT = {
  CART: 'cart',
  MINI_CART: 'mini-cart',
  CHECKOUT: 'checkout',
} as const;
export type RemoveFromCartContext = (typeof REMOVE_FROM_CART_CONTEXT)[keyof typeof REMOVE_FROM_CART_CONTEXT];

export const PURCHASE_CONTEXT = {
  CHECKOUT: 'checkout',
  ONE_CLICK: 'one-click',
  SUBSCRIPTION: 'subscription',
} as const;
export type PurchaseContext = (typeof PURCHASE_CONTEXT)[keyof typeof PURCHASE_CONTEXT];

export const SIGNUP_CONTEXT = {
  HOMEPAGE: 'homepage',
  CHECKOUT: 'checkout',
  POPUP: 'popup',
  REFERRAL: 'referral',
} as const;
export type SignupContext = (typeof SIGNUP_CONTEXT)[keyof typeof SIGNUP_CONTEXT];

export const REFERRAL_CONTEXT = {
  SHARE_LINK: 'share-link',
  EMAIL: 'email',
  SOCIAL: 'social',
} as const;
export type ReferralContext = (typeof REFERRAL_CONTEXT)[keyof typeof REFERRAL_CONTEXT];

export const REWARD_REDEMPTION_CONTEXT = {
  CHECKOUT: 'checkout',
  CART: 'cart',
} as const;
export type RewardRedemptionContext = (typeof REWARD_REDEMPTION_CONTEXT)[keyof typeof REWARD_REDEMPTION_CONTEXT];

export const REVIEW_WRITE_CONTEXT = {
  PDP: 'pdp',
  ORDER_HISTORY: 'order-history',
  EMAIL_PROMPT: 'email-prompt',
} as const;
export type ReviewWriteContext = (typeof REVIEW_WRITE_CONTEXT)[keyof typeof REVIEW_WRITE_CONTEXT];

export const HOME_PAGE_VIEW_CONTEXT = {
  HOME: 'home',
} as const;
export type HomePageViewContext = (typeof HOME_PAGE_VIEW_CONTEXT)[keyof typeof HOME_PAGE_VIEW_CONTEXT];

// === Source ===

/**
 * Traffic source classifications for events.
 * Indicates how the user arrived at the current page/event.
 *
 * @constant
 * @property {string} DIRECT - Direct traffic (typed URL, bookmark, etc.)
 * @property {string} SEARCH - From search results
 * @property {string} CATEGORY - From category page
 * @property {string} RECOMMENDATION - From recommendation section
 * @property {string} PROMOTION - From promotional campaign
 * @property {string} CART - From shopping cart
 * @property {string} WISHLIST - From wishlist
 * @property {string} EMAIL - From email campaign
 * @property {string} PUSH - From push notification
 * @property {string} SNS - From social media
 */
export const SOURCE = {
  DIRECT: 'direct',
  SEARCH: 'search',
  CATEGORY: 'category',
  RECOMMENDATION: 'recommendation',
  PROMOTION: 'promotion',
  CART: 'cart',
  WISHLIST: 'wishlist',
  EMAIL: 'email',
  PUSH: 'push',
  SNS: 'sns',
} as const;

/**
 * Union type of all source values.
 */
export type Source = (typeof SOURCE)[keyof typeof SOURCE];

// === Device Type ===
/**
 * Device type classifications.
 * Automatically detected based on viewport width:
 * - MOBILE: < 768px
 * - TABLET: 768px - 1159px
 * - DESKTOP: >= 1160px
 *
 * @constant
 * @property {string} MOBILE - Mobile device
 * @property {string} TABLET - Tablet device
 * @property {string} DESKTOP - Desktop device
 */
export const DEVICE_TYPE = {
  MOBILE: 'mobile',
  TABLET: 'tablet',
  DESKTOP: 'desktop',
} as const;

/**
 * Union type of all device type values.
 */
export type DeviceType = (typeof DEVICE_TYPE)[keyof typeof DEVICE_TYPE];

// === Storage keys & timeouts ===

/**
 * Storage keys for personalization data.
 *
 * @constant
 * @property {string} ACTOR_ID - Key for actor (user) ID in localStorage
 * @property {string} SESSION_ID - Key for session ID in sessionStorage (sent to server)
 * @property {string} SESSION_TS - Key for session timestamp in sessionStorage (client-side only)
 */
export const STORAGE_KEYS = {
  ACTOR_ID: 'ss_actor_id',
  SESSION_ID: 'ss_p_session_id',
  SESSION_TS: 'ss_p_session_ts',
} as const;

/**
 * Session timeout duration in milliseconds.
 * Sessions expire after 30 minutes of inactivity.
 *
 * @constant
 * @default 1800000 (30 minutes)
 */
export const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

/**
 * Maximum number of events to queue before actor ID is resolved.
 * Events beyond this limit are dropped to prevent memory issues.
 *
 * @constant
 * @default 100
 */
export const EVENT_QUEUE_MAX_SIZE = 100;

/**
 * Event types considered high-value that warrant retry on failure.
 * These events represent monetary transactions that should not be silently lost.
 *
 * @constant
 */
export const HIGH_VALUE_EVENT_TYPES: ReadonlySet<string> = new Set([
  EVENT_TYPE.PURCHASE,
]);

/**
 * Retry configuration for high-value event delivery.
 * Uses exponential backoff: delay = BASE_DELAY_MS * 2^(attempt-1)
 *
 * @constant
 * @property {number} MAX_ATTEMPTS - Maximum number of send attempts (including initial)
 * @property {number} BASE_DELAY_MS - Base delay in milliseconds for first retry
 */
export const RETRY_CONFIG = {
  MAX_ATTEMPTS: 3,
  BASE_DELAY_MS: 1000,
} as const;
